import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId, GridRowModes, GridRowModesModel } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { getMinSpendProductTypeOptions, getProductTypeOptions } from '~/services/PromoService';

import { ProductMultiSelectEditCell } from './ProductMultiSelectEditCell';
import { NumberEditCell, getProductLabel } from './VoucherRewardEditCells';

type VoucherReward = definitions['VoucherRewardV1'];

type Props = {
  rewards: VoucherReward[];
  onChange: (rewards: VoucherReward[]) => void;
  disabled?: boolean;
  showAdvanceDescription?: boolean;
};

/**
 * The calc does not yet support product specific min spend, or product specific reward targeting
 */
const DISABLE_SUPPORT_FOR_PRODUCT_SPECIFIC_MIN_SPEND = true;

export default function VoucherRewards({ rewards, onChange, showAdvanceDescription, disabled = false }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const promoProductTypes = promoMeta?.promo_product_types ?? [];

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [newRewardIds, setNewRewardIds] = useState<string[]>([]);

  useEffect(() => {
    setNewRewardIds((prev) => prev.filter((id) => rewards.some((reward) => reward.id_voucher_reward === id)));
  }, [rewards]);

  const productLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    const options = getProductTypeOptions(promoProductTypes, []);
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [promoProductTypes]);

  const minSpendProductLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    const options = getMinSpendProductTypeOptions(promoProductTypes, []);
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [promoProductTypes]);

  const getDefaultProducts = useCallback(() => {
    const defaults = promoProductTypes.flatMap((category) => {
      const allProduct = category.products.find((product) => product.product_bk === 'ALL' && !product.dontShowInAdmin);
      if (!allProduct) {
        return [];
      }
      return [`${category.category_bk}-${allProduct.product_bk}` as VoucherReward['products'][number]];
    });
    return defaults;
  }, [promoProductTypes]);

  const handleAddReward = useCallback(() => {
    const newId = uuid();
    const fallbackReward = rewards[0];
    const defaultProducts =
      fallbackReward?.products && fallbackReward.products.length > 0 ? fallbackReward.products : getDefaultProducts();
    const defaultRewardAmount = Number.isFinite(fallbackReward?.reward_amount) ? fallbackReward?.reward_amount : 50;
    onChange([
      ...rewards,
      {
        id_voucher_reward: newId,
        reward_amount: defaultRewardAmount,
        products: defaultProducts,
        min_spend: 500,
        min_spend_products: fallbackReward?.min_spend_products ?? [],
      },
    ]);
    setNewRewardIds((prev) => [...prev, newId]);
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'reward_amount' } }));
  }, [getDefaultProducts, onChange, rewards]);

  const handleEditClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      if (newRewardIds.includes(String(id))) {
        onChange(rewards.filter((reward) => reward.id_voucher_reward !== id));
        setNewRewardIds((prev) => prev.filter((rewardId) => rewardId !== id));
      }
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    },
    [newRewardIds, onChange, rewards],
  );

  const handleDeleteReward = useCallback(
    (rewardId: string) => {
      const next = rewards.filter((reward) => reward.id_voucher_reward !== rewardId);
      onChange(next);
    },
    [onChange, rewards],
  );

  const validateReward = useCallback(
    (reward: VoucherReward) => {
      if (!Number.isFinite(reward.reward_amount) || Number(reward.reward_amount) <= 0) {
        enqueueSnackbar('Reward amount must be greater than 0', { variant: 'error' });
        return false;
      }
      if (!reward.products || reward.products.length === 0) {
        enqueueSnackbar('At least one product must be selected for the reward', { variant: 'error' });
        return false;
      }
      return true;
    },
    [enqueueSnackbar],
  );

  const processRowUpdate = useCallback(
    (updatedRow: VoucherReward) => {
      if (!validateReward(updatedRow)) {
        throw new Error('Validation failed');
      }
      const next = rewards.map((reward) =>
        reward.id_voucher_reward === updatedRow.id_voucher_reward ? updatedRow : reward,
      );
      onChange(next);
      setNewRewardIds((prev) => prev.filter((id) => id !== updatedRow.id_voucher_reward));
      return updatedRow;
    },
    [onChange, rewards, validateReward],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update reward', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const columns = useMemo<Array<GridColDef<VoucherReward>>>(
    () => [
      {
        field: 'reward_amount',
        headerName: 'Reward Amount',
        minWidth: 140,
        flex: 1,
        editable: true,
        renderEditCell: (params) => <NumberEditCell {...params} />,
      },
      {
        field: 'products',
        headerName: 'Products',
        minWidth: 220,
        flex: 2,
        editable: true,
        renderCell: (params) => {
          const values = params.row.products ?? [];
          if (!values.length) {
            return '-';
          }
          return values.map((value) => getProductLabel(value, productLabelMap)).join(', ');
        },
        renderEditCell: (params) => (
          <ProductMultiSelectEditCell
            {...params}
            disabled={false}
            promoProductTypes={promoProductTypes}
            multiSelectType="products"
          />
        ),
      },
      {
        field: 'min_spend',
        headerName: 'Min Spend',
        minWidth: 120,
        flex: 1,
        editable: true,
        renderCell: (params) => (params.row.min_spend == null ? '-' : params.row.min_spend),
        renderEditCell: (params) => <NumberEditCell {...params} disabled={false} />,
      },
      {
        field: 'min_spend_products',
        headerName: 'Min Spend Products',
        minWidth: 220,
        flex: 2,
        editable: !DISABLE_SUPPORT_FOR_PRODUCT_SPECIFIC_MIN_SPEND,
        renderCell: (params) => {
          const values = params.row.min_spend_products ?? [];
          if (!values.length) {
            return '-';
          }
          return values.map((value) => getProductLabel(value, minSpendProductLabelMap)).join(', ');
        },
        renderEditCell: (params) => (
          <ProductMultiSelectEditCell
            {...params}
            disabled={DISABLE_SUPPORT_FOR_PRODUCT_SPECIFIC_MIN_SPEND}
            promoProductTypes={promoProductTypes}
            multiSelectType="min_spend_products"
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isEditing = rowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={1}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save reward"
                    size="small"
                    onClick={() => handleSaveClick(params.id)}
                    disabled={disabled}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel reward edit"
                    size="small"
                    onClick={() => handleCancelClick(params.id)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    aria-label="Edit reward"
                    size="small"
                    onClick={() => handleEditClick(params.id)}
                    disabled={disabled}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Delete reward"
                    size="small"
                    onClick={() => handleDeleteReward(params.row.id_voucher_reward)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          );
        },
      },
    ],
    [
      disabled,
      handleCancelClick,
      handleDeleteReward,
      handleEditClick,
      handleSaveClick,
      minSpendProductLabelMap,
      productLabelMap,
      promoProductTypes,
      rowModesModel,
    ],
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        {showAdvanceDescription && (
          <Typography variant="body2" color="text.secondary">
            The amount the voucher is worth, and for which products
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddReward}
          disabled={disabled || isPromoMetaLoading}
        >
          Add reward
        </Button>
      </Stack>
      <DataGrid
        rows={rewards}
        columns={columns}
        getRowId={(row) => row.id_voucher_reward}
        disableRowSelectionOnClick
        hideFooter
        loading={isPromoMetaLoading}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        sx={{ backgroundColor: 'background.paper' }}
      />
    </Box>
  );
}
